import React from "react";
import { Link } from "react-router-dom";

/**
 * Postun Hazır sub-brand logo — uses the OFFICIAL PNG asset shipped at
 * `/brand/postun-hazir-logo.png` rather than a hand-built sticker. This guarantees
 * 1:1 brand fidelity with what the agency uses on Instagram & social.
 *
 * Variants:
 *   - "full"    → PNG with caption "by Yastık Medya" underneath (default)
 *   - "compact" → just the PNG mark
 *   - "sticker" → PNG mark inside a soft pill background (used in headers / CTAs)
 */
export default function PostunHazirLogo({
  to = "/",
  size = 26,
  variant = "full",
  tone = "default",          // "default" | "cream" (when placed on dark bg)
  className = "",
  testId = "postun-hazir-logo",
  asLink = true,
}) {
  const captionClass = tone === "cream" ? "text-postun-yellow" : "text-postun-blue/60";

  let inner;
  if (variant === "sticker") {
    const pillBg = tone === "cream"
      ? "bg-cream/95"
      : "bg-white border border-postun-blue/15";
    inner = (
      <span
        className={`inline-flex items-center gap-2 rounded-full ${pillBg} pl-1.5 pr-3 py-1 shadow-[0_6px_22px_-10px_rgba(37,99,235,0.55)] ${className}`}
      >
        <img
          src="/brand/postun-hazir-logo.png"
          alt="Postun Hazır"
          loading="lazy"
          className="object-contain"
          style={{ width: size + 4, height: size + 4 }}
        />
        <span
          className="font-display font-extrabold lowercase leading-none text-postun-blue-deep"
          style={{ fontSize: `${size * 0.62}px`, letterSpacing: "-0.03em" }}
        >
          postun&nbsp;hazır
        </span>
      </span>
    );
  } else if (variant === "compact") {
    inner = (
      <span className={`inline-flex items-center select-none ${className}`}>
        <img
          src="/brand/postun-hazir-logo.png"
          alt="Postun Hazır"
          loading="lazy"
          className="object-contain"
          style={{ width: size + 8, height: size + 8 }}
        />
      </span>
    );
  } else {
    // full
    inner = (
      <span className={`group inline-flex items-center gap-3 select-none ${className}`}>
        <img
          src="/brand/postun-hazir-logo.png"
          alt="Postun Hazır"
          loading="lazy"
          className="object-contain transition-transform duration-500 group-hover:rotate-[6deg] group-hover:scale-[1.04]"
          style={{ width: size + 14, height: size + 14 }}
        />
        <span className="flex flex-col leading-none">
          <span
            className="sr-only"
          >
            Postun Hazır
          </span>
          <span className={`mt-0.5 text-[0.55rem] uppercase tracking-[0.32em] ${captionClass}`}>
            by Yastık Medya
          </span>
        </span>
      </span>
    );
  }

  if (!asLink) return inner;
  return (
    <Link to={to} data-testid={testId} aria-label="Postun Hazır" className="inline-flex">
      {inner}
    </Link>
  );
}
